import type { JSONContent } from "@tiptap/core";
import { z } from "zod";
import {
  loreCanonStatuses,
  loreEntryTypes,
  type LoreCanonStatus,
  type LoreEntryType,
} from "@/types/lore";

function isJsonDocument(value: unknown): value is JSONContent {
  return typeof value === "object" && value !== null && "type" in (value as Record<string, unknown>);
}

function isFileLike(value: unknown) {
  return value === undefined
    || value === null
    || (typeof File !== "undefined" && value instanceof File);
}

const coverImageSchema = z.custom<File | null | undefined>(isFileLike, {
  message: "lore.messages.saveError",
});

const jsonDocumentSchema = z.custom<JSONContent>(isJsonDocument, {
  message: "lore.messages.saveError",
});

const loreTypeSchema = z.enum(loreEntryTypes);
const loreCanonStatusSchema = z.enum(loreCanonStatuses);

export const projectDocumentSchema = z.object({
  body: jsonDocumentSchema,
});

export const createLoreEntrySchema = z.object({
  title: z.string().trim().min(1, "auth.validation.required").max(160, "auth.validation.required"),
  type: loreTypeSchema,
  body: jsonDocumentSchema,
  localRules: jsonDocumentSchema,
  canonStatus: loreCanonStatusSchema.default("draft"),
  coverImageFile: coverImageSchema.optional(),
});

export const updateLoreEntrySchema = z.object({
  title: z.string().trim().min(1, "auth.validation.required").max(160, "auth.validation.required"),
  type: loreTypeSchema,
  body: jsonDocumentSchema,
  localRules: jsonDocumentSchema,
  coverImageFile: coverImageSchema.optional(),
  removeCoverImage: z.boolean().optional(),
});

export type ProjectDocumentInput = z.output<typeof projectDocumentSchema>;
export type CreateLoreEntryValues = z.input<typeof createLoreEntrySchema>;
export type CreateLoreEntryFormInput = z.output<typeof createLoreEntrySchema>;
export type UpdateLoreEntryValues = z.input<typeof updateLoreEntrySchema>;
export type UpdateLoreEntryFormInput = z.output<typeof updateLoreEntrySchema>;
export type LoreTypeInput = LoreEntryType;
export type LoreCanonStatusInput = LoreCanonStatus;
